import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import api, { getToken, setToken } from '../../services/api.js';
import { useToast } from '../../context/ToastContext.jsx';
import logo from '../../assets/logo.jpg';
import Dashboard from './Dashboard.jsx';
import { ProductList, ProductForm } from './Products.jsx';
import Orders from './Orders.jsx';
import { SettingsPage, BannersPage } from './Settings.jsx';

function Login({ onDone }) {
  const notify = useToast();
  const [form, setForm] = useState({ username: '', password: '', otp: '' });
  const [needOtp, setNeedOtp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.login(form.username.trim(), form.password, form.otp.trim() || undefined);
      notify('Signed in', 'success');
      onDone();
    } catch (err) {
      const offline = /failed to fetch|networkerror|load failed|took too long/i.test(err.message);
      setError(offline ? 'Cannot reach the server. Check that the backend is running.' : err.message);
      if (/invalid credentials/i.test(err.message)) setNeedOtp(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="alogin">
      <form className="alogin__box" onSubmit={submit}>
        <img src={logo} alt="Logo" />
        <h1>Admin</h1>
        <label>Username<input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} autoComplete="username" required /></label>
        <label>Password<input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="current-password" required /></label>
        {needOtp && <label>One-time code (if enabled)<input value={form.otp} onChange={(e) => setForm({ ...form, otp: e.target.value })} autoComplete="one-time-code" /></label>}
        {error && <p className="aerror" role="alert">{error}</p>}
        <button type="submit" className="abtn abtn--block" disabled={busy}>{busy ? 'Signing in' : 'Sign in'}</button>
      </form>
    </div>
  );
}

function Layout({ onLogout }) {
  const [open, setOpen] = useState(false);
  const links = [
    ['/admin', 'Dashboard', true],
    ['/admin/products', 'Products'],
    ['/admin/orders', 'Orders'],
    ['/admin/banners', 'Banners'],
    ['/admin/settings', 'Settings'],
  ];
  return (
    <div className="admin">
      <header className="admin__top">
        <button type="button" className="abtn abtn--ghost admin__menu" onClick={() => setOpen((o) => !o)}>{open ? 'Close' : 'Menu'}</button>
        <img src={logo} alt="Logo" />
        <span>Admin</span>
      </header>
      <aside className={`admin__side${open ? ' is-open' : ''}`}>
        <div className="admin__brand"><img src={logo} alt="Logo" /><span>Admin</span></div>
        <nav onClick={() => setOpen(false)}>
          {links.map(([to, label, end]) => <NavLink key={to} to={to} end={end}>{label}</NavLink>)}
          <a href="/" target="_blank" rel="noreferrer">View store</a>
        </nav>
        <button type="button" className="abtn abtn--ghost admin__logout" onClick={onLogout}>Sign out</button>
      </aside>
      <main className="admin__main">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="products" element={<ProductList />} />
          <Route path="products/new" element={<ProductForm />} />
          <Route path="products/:id" element={<ProductForm />} />
          <Route path="orders" element={<Orders />} />
          <Route path="banners" element={<BannersPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function AdminApp() {
  const navigate = useNavigate();
  const [state, setState] = useState(getToken() ? 'checking' : 'out');

  const verify = useCallback(async () => {
    if (!getToken()) { setState('out'); return; }
    try { await api.me(); setState('in'); } catch { setToken(null); setState('out'); }
  }, []);

  useEffect(() => { verify(); }, [verify]);
  useEffect(() => {
    const out = () => setState('out');
    window.addEventListener('elegance:unauthorized', out);
    return () => window.removeEventListener('elegance:unauthorized', out);
  }, []);

  const logout = () => { setToken(null); setState('out'); navigate('/admin'); };

  if (state === 'checking') return <div className="boot" aria-busy="true" />;
  if (state === 'out') return <Login onDone={() => setState('in')} />;
  return <Layout onLogout={logout} />;
}
